import {
  Text,
  Flex,
  Grid,
  GridItem,
  Link,
  Button,
  IconButton,
  Spinner,
  Badge,
  Center,
  VStack,
  Box,
  Image,
  useColorModeValue,
  SimpleGrid,
  Spacer,
  useBreakpointValue,
} from '@chakra-ui/react'
import React, { FC } from 'react'
import {
  formatDateForDisplay,
  formatFloatForDisplay,
} from '../../../utils/utils'
import { pointsMultiplierType } from '../../techRaffles/types'
import { FiPlusCircle } from 'react-icons/fi'
import { trpc } from '../../../utils/trpc'
import { DeleteIcon } from '@chakra-ui/icons'
import toast from 'react-hot-toast'

const PointMultiplierCard: FC<{
  pointMultiplier: pointsMultiplierType
  isAdminView?: boolean
  refetch: () => void
}> = ({ pointMultiplier, isAdminView, refetch }) => {
  const borderColor = useColorModeValue('#E9E9E9', '#393e43')
  const textColor = useColorModeValue('black', 'white')
  const circleBgColor = useColorModeValue('gray.50', 'gray.900')
  const dateBannerBgColor = useColorModeValue('gray.100', 'gray.700')

  const layout = useBreakpointValue({ base: 'mobile', md: 'desktop' })

  const deletePointMultiplierMut = trpc.useMutation('pointsMultiplier.delete', {
    onSuccess: () => {
      toast.success('Point multiplier deleted successfully')
      refetch()
    },
    onError: (err) => {
      toast.error(err.message)
    },
  })

  const TimingStateBadge = () => {
    if (pointMultiplier.starts > new Date()) {
      return (
        <Badge colorScheme='green'>
          Starting in{' '}
          {pointMultiplier.starts.getTime() - new Date().getTime() <
          1000 * 60 * 60
            ? `${Math.floor(
                (pointMultiplier.starts.getTime() - new Date().getTime()) /
                  (1000 * 60)
              )} minutes`
            : `${Math.floor(
                (pointMultiplier.starts.getTime() - new Date().getTime()) /
                  (1000 * 60 * 60)
              )} hours`}
        </Badge>
      )
    }

    if (
      pointMultiplier.starts < new Date() &&
      pointMultiplier.ends > new Date()
    ) {
      return (
        <Badge colorScheme='blue'>
          Running for{' '}
          {pointMultiplier.starts < new Date() &&
            pointMultiplier.ends > new Date() &&
            (pointMultiplier.ends.getTime() - new Date().getTime() <
            1000 * 60 * 60
              ? `${Math.floor(
                  (pointMultiplier.ends.getTime() - new Date().getTime()) /
                    (1000 * 60)
                )} minutes`
              : `${Math.floor(
                  (pointMultiplier.ends.getTime() - new Date().getTime()) /
                    (1000 * 60 * 60)
                )} hours`)}
        </Badge>
      )
    }

    if (pointMultiplier.ends < new Date()) {
      return <Badge colorScheme='red'>Ended</Badge>
    }
  }

  return (
    <Box
      border='1px solid'
      borderColor={borderColor}
      borderRadius='md'
      paddingY='1rem'
      paddingX='1.5rem'
      width='100%'
      backgroundColor={useColorModeValue('white', 'cardBlack')}
    >
      <Grid
        width='100%'
        gap={3}
        templateRows={
          isAdminView
            ? ['repeat(3, 1fr)', 'repeat(1, 1fr)']
            : ['repeat23, 1fr)', 'repeat(1, 1fr)']
        }
        templateColumns={
          isAdminView
            ? ['repeat(1, 1fr)', 'repeat(3, 1fr)']
            : ['repeat(1, 1fr)', 'repeat(2, 1fr)']
        }
      >
        {/* Image */}
        <GridItem>
          <VStack paddingY={1} alignItems='center' justifyContent={'center'}>
            {pointMultiplier.type !== 'TOKEN' && (
              <Image
                borderRadius='full'
                boxSize='80px'
                objectFit='cover'
                src={
                  (pointMultiplier.type === 'COLLECTION'
                    ? pointMultiplier.applicableCollectionForMultiplier?.image
                    : pointMultiplier.type === 'COMMUNITY'
                    ? pointMultiplier.appplicableProjectMembersForMultiplier
                        ?.profilePictureUrl
                    : '')!
                }
                alt='Community/Collection Image'
              />
            )}
            <Text fontWeight='bold' color={textColor} textAlign='center'>
              {pointMultiplier.type === 'COLLECTION'
                ? pointMultiplier.applicableCollectionForMultiplier?.title
                : pointMultiplier.type === 'COMMUNITY'
                ? pointMultiplier.appplicableProjectMembersForMultiplier
                    ?.communityName
                : pointMultiplier.applicableTokenForMultiplier?.symbol}
            </Text>
            <Text color={textColor} textAlign='center'>
              {pointMultiplier.type === 'COLLECTION'
                ? `Raffle an NFT of the collection of ${pointMultiplier.applicableCollectionForMultiplier?.title}`
                : pointMultiplier.type === 'COMMUNITY'
                ? `Tag the following community for the multiplier: ${pointMultiplier.appplicableProjectMembersForMultiplier?.communityName}`
                : `Buy tickets with the ${pointMultiplier.applicableTokenForMultiplier?.symbol} token for the multiplier`}
            </Text>
          </VStack>
        </GridItem>

        {/* Multiplier Info */}
        <GridItem h='100%'>
          <VStack
            h='100%'
            display='flex'
            alignItems='center'
            justifyContent='center'
            spacing='1rem'
          >
            <Flex
              alignItems='center'
              justifyContent='center'
              borderRadius={'2xl'}
              p={4}
              bg={circleBgColor}
              borderWidth='1px'
              borderColor={borderColor}
            >
              <VStack>
                <Image src='/images/rewards/GemsShine.svg' />
                <Text fontSize='3xl' fontWeight='bold' color={textColor}>
                  {formatFloatForDisplay(pointMultiplier.multiplier)}x
                </Text>
              </VStack>
            </Flex>
            <Flex display='flex' alignItems='center' justifyContent='center'>
              {!isAdminView && TimingStateBadge()}
            </Flex>
          </VStack>
        </GridItem>

        {/* Community/Collection Details & ID */}
        {isAdminView && (
          <GridItem
            display='flex'
            flexDirection='column'
            justifyContent='center'
            alignItems='center'
          >
            <VStack spacing='.5rem'>
              <Text color={textColor}>
                ID: {pointMultiplier.id} / TYPE: {pointMultiplier.type}
              </Text>
              <Flex
                borderRadius='md'
                backgroundColor={dateBannerBgColor}
                p={2}
                alignItems='center'
                justifyContent='center'
              >
                <Text color={textColor}>
                  {formatDateForDisplay(pointMultiplier.starts)}
                  {' - '}
                  {formatDateForDisplay(pointMultiplier.ends)}
                </Text>
              </Flex>
              {TimingStateBadge()}
              <IconButton
                aria-label='Delete'
                icon={<DeleteIcon />}
                onClick={() =>
                  deletePointMultiplierMut.mutate({ id: pointMultiplier.id })
                }
              />
            </VStack>
          </GridItem>
        )}
      </Grid>
    </Box>
  )
}

export default PointMultiplierCard
